import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ActiveSearch, ChatMessage, ChatStatus } from '../hooks/useChat';
import type { Citation } from '../types/api';
import type { ProgressStep } from '../utils/progress';
import ChoiceCard from './ChoiceCard';
import ErrorBoundary from './ErrorBoundary';
import { ErrorMessage, FinishedMessage, InProgressMessage, StreamingMessage } from './Message';
import styles from './MessageList.module.css';

// One malformed message (an unexpected citation shape, a markdown or KaTeX throw)
// degrades to this and leaves the rest of the conversation readable. Short because
// .errorBody does not wrap.
const messageFallback = <ErrorMessage message="This message could not be displayed." />;

interface Props {
  messages: ChatMessage[];
  sessionId: string;
  status: ChatStatus;
  statusMessage: string;
  activeSearches: ActiveSearch[];
  progress: ProgressStep[];
  streamingContent: string;
  streamingCitations: Citation[];
  error: string | null;
  onOpenContext: (citations: Citation[]) => void;
  onDocLinkClick?: (url: string, citations: Citation[]) => void;
  activeDocUrl?: string | null;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  /** Sends a clicked option of a setup question as the user's message (#489). */
  onChoose?: (label: string) => void;
  onTypeOwn?: () => void;
  /** No option can be sent, for example because the conversation's docs are withdrawn. */
  choicesDisabled?: boolean;
}

export default function MessageList({
  messages,
  sessionId,
  status,
  statusMessage,
  activeSearches,
  progress,
  streamingContent,
  streamingCitations,
  error,
  onOpenContext,
  onDocLinkClick,
  activeDocUrl,
  scrollContainerRef,
  onChoose,
  onTypeOwn,
  choicesDisabled,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollContainerRef ?? internalScrollRef;
  const userScrolledRef = useRef(false);
  const prevMsgCountRef = useRef(messages.length);
  const isStreamActive = status !== 'idle' && status !== 'pending' && status !== 'error';
  const [localDraining, setLocalDraining] = useState(false);

  // Every citation seen in this conversation. Lets a history-only answer (which
  // carries no citations of its own) still route its links to the side panel.
  const historyCitations = useMemo(() => messages.flatMap((m) => m.citations ?? []), [messages]);

  const lastMsg = messages[messages.length - 1];
  const isStopped = lastMsg?.role === 'assistant' && lastMsg?.status === 'stopped';

  // prevIsStreamActive.current is the value from the *previous* render (effect hasn't
  // fired yet), so startingDrain is true in the exact render where isStreamActive first
  // becomes false. This closes the one-render gap where showStreaming would otherwise be
  // false||false before the localDraining effect fires, preventing StreamingMessage from
  // unmounting and losing its typewriter state.
  const prevIsStreamActive = useRef(isStreamActive);
  const startingDrain = prevIsStreamActive.current && !isStreamActive;
  const showStreaming =
    !isStopped && status !== 'error' && (isStreamActive || localDraining || startingDrain);

  // When the stream ends, keep StreamingMessage mounted until its buffer drains.
  // When a new stream starts during drain, reset so the next end-of-stream works correctly.
  useEffect(() => {
    if (!prevIsStreamActive.current && isStreamActive) {
      setLocalDraining(false);
    } else if (prevIsStreamActive.current && !isStreamActive && status !== 'error') {
      setLocalDraining(true);
    }
    prevIsStreamActive.current = isStreamActive;
  }, [isStreamActive, status]);

  // When stopped, skip the drain so the stopped notice appears immediately.
  useEffect(() => {
    if (isStopped && localDraining) {
      setLocalDraining(false);
    }
  }, [isStopped, localDraining]);

  // While draining (or about to drain), hide the last message — StreamingMessage shows it.
  // Never slice on error or stop: the stopped/user message should show immediately.
  const messagesToShow =
    !isStopped && status !== 'error' && (localDraining || startingDrain)
      ? messages.slice(0, -1)
      : messages;

  // Force StreamingMessage to remount on each new user turn so display state is fresh
  const userMsgCount = messages.filter((m) => m.role === 'user').length;

  // biome-ignore lint/correctness/useExhaustiveDependencies: setup-once listener — scrollRef.current is set after first commit and stable for the component's lifetime
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userScrolledRef.current = distFromBottom > 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Re-enable auto-scroll whenever the user submits a new message.
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current) {
      const last = messages[messages.length - 1];
      if (last?.role === 'user') userScrolledRef.current = false;
    }
    prevMsgCountRef.current = messages.length;
  }, [messages]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional fire-on-change — body uses only refs; deps drive when to re-evaluate scroll position
  useEffect(() => {
    if (!userScrolledRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingContent, status]);

  // The options stay clickable only under the last message. Once answered, the card
  // folds to the option the user picked, and disappears if they typed something else.
  const choiceCard = (choices: NonNullable<ChatMessage['choices']>, next?: ChatMessage) => {
    if (!next) {
      return (
        <ChoiceCard
          choices={choices}
          onChoose={onChoose}
          onTypeOwn={onTypeOwn}
          disabled={choicesDisabled || status !== 'idle'}
        />
      );
    }
    const picked = choices.find((c) => c.label === next.content);
    return next.role === 'user' && picked ? (
      <ChoiceCard choices={choices} chosen={picked.label} />
    ) : null;
  };

  return (
    <div className={styles.messages}>
      <div className={styles.topRoof} />
      {(() => {
        const lastAssistantIdx = messagesToShow.reduce(
          (last, msg, i) => (msg.role === 'assistant' ? i : last),
          -1,
        );
        return messagesToShow.map((msg, idx) => {
          let content: React.ReactNode;
          if (msg.role === 'assistant' && msg.status === 'in_progress') {
            content = <InProgressMessage statusEvents={msg.statusEvents} />;
          } else if (msg.role === 'assistant' && msg.status === 'error') {
            content = <ErrorMessage message="Sorry, something went wrong. Please try again." />;
          } else {
            content = (
              <FinishedMessage
                message={msg}
                sessionId={sessionId}
                onOpenContext={onOpenContext}
                onDocLinkClick={onDocLinkClick}
                activeDocUrl={activeDocUrl}
                historyCitations={historyCitations}
                isLast={idx === lastAssistantIdx}
                after={msg.choices && choiceCard(msg.choices, messagesToShow[idx + 1])}
              />
            );
          }
          // The key moves to the boundary because the boundary is now the list element.
          // It stays 1:1 with the message, so identity is unchanged.
          return (
            <ErrorBoundary key={msg.id} label="message" fallback={messageFallback}>
              {content}
            </ErrorBoundary>
          );
        });
      })()}

      {/* userMsgCount keys the boundary rather than StreamingMessage: remounting the
          boundary remounts its child, so the typewriter still resets on a new turn,
          and a crash on one turn no longer sticks for the rest of the conversation. */}
      {showStreaming && (
        <ErrorBoundary key={userMsgCount} label="streaming-message" fallback={messageFallback}>
          <StreamingMessage
            content={streamingContent}
            status={status}
            statusMessage={statusMessage}
            activeSearches={activeSearches}
            progress={progress}
            streamingCitations={streamingCitations}
            onOpenContext={onOpenContext}
            onDocLinkClick={onDocLinkClick}
            activeDocUrl={activeDocUrl}
            historyCitations={historyCitations}
            onDrainComplete={() => setLocalDraining(false)}
          />
        </ErrorBoundary>
      )}

      {status === 'error' && error && <ErrorMessage message={error} />}

      <div ref={bottomRef} className={styles.bottom} />
    </div>
  );
}
